import { ReactNode } from 'react';
import { useNarrow } from '../../lib/use-narrow';
import { PageHeader } from './PageHeader';
import { ScrollArea } from './ui';

// Thin content wrapper for Tasks / Pulses / Knowledge. The layout chrome
// (sidebar, brand header, backdrop) lives in AppFrame and persists across
// route changes; AppShell just renders the standard page header (title +
// description) + body inside the content pane to the right of the sidebar.
//
// When given a `title`, AppShell renders it through the shared PageHeader so
// every page reads with the same Display title + 48px gap to content. A page
// that needs a custom hero passes an empty title and renders its own header as
// the first child instead. The optional `breadcrumb` node is rendered above the
// header with the same gap PageHeader uses internally, since AppShell receives
// it pre-rendered (PageHeader's own breadcrumb slot takes a Crumb[]).
export function AppShell({
  title,
  description,
  breadcrumb,
  fluid = false,
  children,
}: {
  // `current` is still accepted for source compatibility but is now derived
  // from the route by AppFrame's Sidebar, so it's unused here.
  current?: string;
  // Optional: when omitted, the page renders its own header (e.g. PageHeader)
  // as the first child and AppShell is a pure layout wrapper.
  title?: string;
  description?: string;
  // A sub-page back-link (chevron-left icon + e.g. "Your agents"), rendered above the title.
  breadcrumb?: ReactNode;
  /** Drop the pane's 900px floor: the screen lays itself out for any width (the map home). */
  fluid?: boolean;
  children: ReactNode;
}) {
  const narrow = useNarrow();
  return (
    <ScrollArea
      component="section"
      className="orion-content-pane"
      data-fluid={fluid ? 'true' : undefined}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        zIndex: 4,
        // Desktop: a wider inline-start gutter so content clears the sidebar/menu
        // rather than hugging it. Narrow: the sidebar is an off-canvas drawer, so
        // that gutter just shoves content over — use symmetric mobile padding
        // (top still clears the fixed brand header / hamburger). Logical so it
        // mirrors in RTL.
        // Horizontal gutter + page width (80px gutters, capped at 1280) come from the shared
        // `.orion-content-pane` rule; only the vertical padding is per-surface.
        // Bottom padding on wide must clear the persistent floating composer
        // (~80px tall at 24px from the bottom). Narrow has no persistent composer.
        // Narrow top = the hamburger/island band (24px inset + 40px icon = 64) plus
        // the 32px gap the Figma leaves to the title (nodes 4176-6999 / 4944-5613).
        paddingBlock: narrow ? '96px 40px' : '72px 120px',
      }}
    >
      <div style={{ width: '100%' }}>
        {/* Desktop-only: the back button replaces the breadcrumb here; mobile uses the header back. */}
        {!narrow && breadcrumb && <div style={{ marginBottom: 'var(--sym-s5)' }}>{breadcrumb}</div>}
        {/* A page can supply an empty title to render its own custom hero (e.g. the Personality
            page's green Maestro tile + name, or a screen with its own PageHeader) in `children`. */}
        {title && <PageHeader title={title} description={description || undefined} />}
        {children}
      </div>
    </ScrollArea>
  );
}
